import React from "react";
import {
  BaseModal,
  BaseModalHeader,
  BaseModalBody,
  Loading,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button/Button";
import { LayersManager } from "baseui/layer";
import { useGetOrderAdditionalInfoQuery } from "@api/graphql/generated/generated-types";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";

interface OrderAdditionalInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  courierId: string;
  orderId: string;
}

/**
 * Loads an order's full additional information key-value pairs on demand (only when opened)
 * via the dedicated orderAdditionalInfo query, so the array is never carried by the shipments
 * list response.
 */
export function OrderAdditionalInfoModal({
  isOpen,
  onClose,
  courierId,
  orderId,
}: OrderAdditionalInfoModalProps) {
  const { showToast } = useCourieStore();
  const { data, loading, error } = useGetOrderAdditionalInfoQuery({
    variables: { courierId, orderId },
    skip: !isOpen,
    fetchPolicy: "no-cache",
    onError: (apolloError) => showErrorToast(apolloError, showToast),
  });

  const pairs = data?.orderAdditionalInfo ?? [];

  return (
    <LayersManager zIndex={20}>
      <BaseModal isOpen={isOpen} onClose={onClose}>
        <BaseModalHeader>Additional Information</BaseModalHeader>
        <BaseModalBody>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loading loading={true} />
            </div>
          ) : error ? (
            <div className="py-4 text-sm text-red-500">
              Failed to load additional information.
            </div>
          ) : pairs.length === 0 ? (
            <div className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No additional information.
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[60vh] flex flex-col gap-2">
              {pairs.map((pair, idx) => (
                <div
                  key={idx}
                  className="flex flex-col border-b dark:border-gray-700 pb-2 last:border-b-0"
                >
                  <span className="text-xs text-slate-400 dark:text-gray-500 break-words">
                    {pair.key}
                  </span>
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100 break-words">
                    {pair.value || "-"}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-6">
            <CourieButton color="gray" size="md" onClick={onClose}>
              Close
            </CourieButton>
          </div>
        </BaseModalBody>
      </BaseModal>
    </LayersManager>
  );
}
